import { useTheme, Stack, Paper, Button, Box, Link, CircularProgress, Chip, Divider, FormControl, InputLabel, Select, MenuItem, Typography } from "@mui/material";
import TitlePage from "@components/atoms/TitlePage/TitlePage";
import TitlePaper from "@components/atoms/TitlePaper/TitlePaper";
import DataItem from "@components/atoms/DataItem/DataItem";
import TempData from "@components/atoms/TempData";
import ErrorAlert from "@components/atoms/ErrorAlert/ErrorAlert";
import ConfirmDialog from "@components/molecules/ConfirmDialog/ConfirmDialog";
import LoadingGif from "../../../assets/gifs/loading.gif";
import { Link as ReactRouterLink } from "react-router-dom";
import { IClientDetailUI } from "src/types/organisms";
import { IClient } from "src/types/global";
import DynamicTable from "../DynamicTable/DynamicTable";
import InputText from "@components/atoms/InputText/InputText";
import Salary from "@components/atoms/Salary/Salary";
import { getCurrency } from "@utils/utils";
import { grey } from "@mui/material/colors";
import { useWatch } from "react-hook-form";
import { useState, useEffect } from "react";
const ClientDetailUI = ({
  id,
  fullName,
  identification,
  phone,
  email,
  creationTime,
  generatedPassword,
  isLoading,
  isTransLoading,
  showError,
  closeError,
  isDeleting,
  showDialog,
  handleConfirmDialog,
  handleOpenDialog,
  handleCloseDialog,
  headers,
  rows,
  pages,
  onChangePage,
  emptyMessage,
  showPayDialog,
  setShowPayDialog,
  pay,
  register,
  isPayLoading,
  errorMessage,
  totalPaid,
  totalRequested,
  totalReturned,
  moneyCapAmount,
  consumedAmount,
  availableCapAmount,
  isOverCap,
  isAdmin,
  employeeCode,
  setEmployeeCode,
  applicant,
  setApplicant,
  handleClosePayDialog,
  control,
  paymentMethod,
  setPaymentMethod,
  transferProof,
  setTransferProof
}: IClientDetailUI) => {
  const theme = useTheme();
  const amount = useWatch({
    control,
    name: "amount"
  });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!transferProof) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(transferProof);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [transferProof]);
  const classes = {
    buttonDelete: {
      marginTop: 2,
      backgroundColor: isDeleting ? theme.palette.error.main : null,
      color: isDeleting ? "white" : null
    },
    innerStack: {
      width: 1,
      "& > div:nth-of-type(1)": {
        marginTop: 0
      }
    },
    inputLabel: {
      color: grey[900],
      marginBottom: 0.5
    }
  };
  return <>
      <TitlePage title={`Detalles del cliente ${fullName}`} subtitle="Toda la información sobre el cliente y sus recibos activos en un solo lugar. Para ofrecer un servicio excepcional y a mantener relaciones sólidas." />
      <Stack direction={"row"} marginTop={3} spacing={3} alignItems={"flex-start"}>
        <Paper elevation={12} sx={{
        width: 1,
        padding: "24px"
      }}>
          <TitlePaper title="Información del cliente" subtitle="Detalle completo de este cliente." />
          <Stack direction={"row"} marginTop={3} flexWrap={"wrap"} width={1}>
            {!isLoading && <>
                <DataItem label="Nombre" value={fullName} />
                <DataItem label="Identificación" value={identification || "Sin documento"} />
                <DataItem label="Teléfono" value={phone} />
                <DataItem label="Correo" value={email || "Sin correo electrónico"} />
                <DataItem label="Fecha de creación" value={creationTime} />
                {generatedPassword && <TempData label="Contraseña generada" value={generatedPassword} />}
              </>}
          </Stack>
          <Stack direction={"row"} justifyContent={"center"}>
            <Box component={"img"} display={isLoading ? "block" : "none"} src={LoadingGif} width={170} />
          </Stack>
        </Paper>
        <Paper elevation={12} sx={{
        width: 400,
        padding: "24px",
        height: "fit-content"
      }}>
          <TitlePaper title="Acciones" subtitle="Ten en cuenta que algunas acciones pueden ser irreversibles. Revisar cuidadosamente cada acción antes de confirmarla." />
          {!isDeleting && <>
              <Link component={ReactRouterLink} to={`/clientes/editar/${id}`} state={({
            id,
            fullName,
            identification,
            phone,
            email,
            creationTime,
            moneyCapAmount
          } as IClient)}>
                <Button onClick={() => null} fullWidth sx={{
              marginTop: 1
            }} disabled={isLoading}>
                  Editar
                </Button>
              </Link>
            </>}
          <Button onClick={isDeleting ? () => null : handleOpenDialog} fullWidth sx={classes.buttonDelete} color="error" disabled={isLoading || !isAdmin} endIcon={isDeleting && <CircularProgress thickness={3} size={20} sx={{
          color: "white"
        }} />}>
            {isDeleting ? "Eliminando" : "Eliminar"}
          </Button>
        </Paper>
      </Stack>
      <Stack direction={"row"} marginTop={3} spacing={3} alignItems={"flex-start"}>
        <DynamicTable title="Movimientos del cliente" subtitle="Explicale al cliente los movimientos de su dinero." headers={headers} rows={rows} pages={pages} onChangePage={onChangePage} emptyMessage={emptyMessage} isLoading={isTransLoading} />
        <Stack direction={"column"} spacing={3} sx={{
        width: 400
      }}>
          <Paper elevation={12} sx={{
          width: 1,
          padding: "24px",
          height: "fit-content"
        }}>
            <TitlePaper title="Cupo" subtitle="Estado del cupo de crédito asignado al cliente." />
            {isOverCap && <Chip label="Sobre cupo" size="small" sx={{
            mt: 1,
            mb: 0.5,
            backgroundColor: theme.palette.error.main,
            color: theme.palette.common.white,
            fontWeight: 700
          }} />}
            {moneyCapAmount != null && <Salary label="Cupo asignado" value={getCurrency(moneyCapAmount)} weight={800} />}
            <Salary label="Consumido" value={`${getCurrency(consumedAmount)} / ${moneyCapAmount != null ? getCurrency(moneyCapAmount) : "Sin límite"}`} color={isOverCap ? theme.palette.error.main : undefined} />
            {moneyCapAmount != null && <Salary label="Disponible" value={getCurrency(availableCapAmount ?? 0)} color={isOverCap ? theme.palette.error.main : theme.palette.success.main} />}
          </Paper>
          <Paper elevation={12} sx={{
          width: 1,
          padding: "24px",
          height: "fit-content"
        }}>
            <TitlePaper title="Resumen" subtitle="Te presentamos el estado actual del cliente con respecto a sus movimientos" />
            <Salary label="Saldo pendiente" value={getCurrency(totalRequested - totalPaid - totalReturned)} color={theme.palette.error.main} simple={false} weight={800} />
            <Salary label="Saldo abonado" value={getCurrency(totalPaid)} color={theme.palette.success.main} />
            <Salary label="Saldo devuelto" value={getCurrency(totalReturned)} color={theme.palette.success.main} />
            <Divider />
            <Salary label="Total a pagar" value={getCurrency(totalRequested - totalReturned)} weight={800} />
            {!isDeleting && <Button onClick={() => isDeleting ? null : setShowPayDialog(true)} fullWidth sx={classes.buttonDelete} disabled={isLoading}>
                Abonar
              </Button>}
          </Paper>
        </Stack>
      </Stack>
      <ErrorAlert open={showError} onClose={closeError} message={errorMessage} />
      <ConfirmDialog open={showDialog} cancelAction={handleCloseDialog} confirmAction={handleConfirmDialog} title="Eliminar cliente" subtitle="¿Estás seguro de que deseas eliminar este cliente?" message="Recuerda, si eliminas el cliente no podrás recuperar la información relacionada con este cliente una vez que se haya eliminado." cancelLabel="No eliminar" confirmLabel="Sí, eliminar" />
      <ConfirmDialog open={showPayDialog} cancelAction={handleClosePayDialog} confirmAction={pay} title="Abonar" subtitle="" message="Ingresa el valor que el cliente abona para cualquiera de sus recibos." cancelLabel="Cancelar" confirmLabel="Abonar" autoClose={false} withEnter={true} withEscape={true} contentRender={<Box marginTop={1}>
            <InputText label="Cantidad a abonar" placeholder="Ingresa la cantidad a abonar *" name="amount" register={register("amount", {
        required: true
      })} disabled={isPayLoading} sx={{
        mb: 2
      }} autoFocus={true} />
            <InputText label="Código de empleado" name="employeeCode" placeholder="Ingresa tu código de empleado *" register={register("employeeCode", {
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => setEmployeeCode(e.target.value),
        value: employeeCode || "",
        required: true
      })} type="password" disabled={isPayLoading} sx={{
        mb: 2
      }} />
            <InputText label="Solicitante" name="applicant" placeholder="Ingresa el nombre del solicitante" register={register("applicant", {
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => setApplicant(e.target.value),
        value: applicant || ""
      })} disabled={isPayLoading} sx={{
        mb: 2
      }} />
            <FormControl fullWidth sx={{
        mb: 2
      }} disabled={isPayLoading}>
              <InputLabel id="client-payment-method-label">
                Método de pago
              </InputLabel>
              <Select labelId="client-payment-method-label" value={paymentMethod} label="Método de pago" onChange={e => {
          setPaymentMethod(Number(e.target.value));
          setTransferProof(null);
        }} SelectDisplayProps={({
          'data-testid': 'payment-method-select'
        } as any)}>
                <MenuItem value={0}>Efectivo</MenuItem>
                <MenuItem value={1}>Transferencia</MenuItem>
              </Select>
            </FormControl>
            {paymentMethod === 1 && <Box sx={{
        mb: 2
      }}>
                <Typography variant="body2" sx={{
          mb: 1,
          fontWeight: 500
        }}>
                  Comprobante de transferencia *
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Button component="label" variant="outlined" size="small" disabled={isPayLoading} data-testid="transfer-proof-upload" sx={{
            flexShrink: 0,
            textTransform: "none"
          }}>
                    {transferProof ? "Cambiar imagen" : "Adjuntar imagen"}
                    <input type="file" hidden accept="image/*" disabled={isPayLoading} onChange={e => setTransferProof(e.target.files?.[0] ?? null)} />
                  </Button>
                  {previewUrl && <Box component="img" src={previewUrl} alt="preview" sx={{
            width: 48,
            height: 48,
            objectFit: "cover",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider"
          }} />}
                  {transferProof && <Typography variant="caption" noWrap sx={{
            flex: 1,
            minWidth: 0
          }}>
                      {transferProof.name}
                    </Typography>}
                </Stack>
              </Box>}
          </Box>} disabled={!employeeCode.trim() || !amount || isPayLoading || paymentMethod === 1 && !transferProof} />
    </>;
};
export default ClientDetailUI;